import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Send, Loader2, CheckCircle2, ChevronDown } from 'lucide-react'
import KineticButton from '../ui/KineticButton'
import { api } from '../../lib/api'
import { COMPANY } from '../../data/company'
import { PROJECT_TYPES } from '../../utils/constants'

const emptyForm = { name: '', email: '', company: '', projectType: '', message: '', website: '' }

// Outlined text field: label above, corners that round off further and a
// ring that springs out when the field takes focus.
function Field({ label, name, as = 'input', children, ...props }) {
  const Tag = as
  return (
    <label className="field">
      <span>{label}</span>
      <div className="field-control">
        <Tag name={name} {...props}>
          {children}
        </Tag>
        {as === 'select' && <ChevronDown className="chevron h-4 w-4" />}
      </div>
    </label>
  )
}

// Shared contact form — rendered inline on desktop and inside a BottomSheet on mobile.
// Submits to /api/contact so messages land in the admin inbox; if the API is
// unreachable it falls back to the visitor's mail client so nothing is lost.
const ContactForm = ({ onSubmitted, className = '' }) => {
  const [form, setForm] = useState(emptyForm)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  const mailtoFallback = () => {
    const subject = encodeURIComponent(`New Project Inquiry: ${form.projectType || 'General'}`)
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nProject Type: ${form.projectType}\n\nMessage:\n${form.message}`
    )
    window.location.href = `mailto:${COMPANY.contact.email}?subject=${subject}&body=${body}`
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      await api.contact(form)
      setSent(true)
    } catch {
      mailtoFallback()
    } finally {
      setBusy(false)
      onSubmitted?.()
    }
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {sent ? (
        <motion.div
          key="sent"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={`flex flex-col items-center justify-center gap-3 py-10 text-center ${className}`}
        >
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-tertiary-container text-tertiary-on-container">
            <CheckCircle2 className="h-7 w-7" />
          </span>
          <p className="text-xl font-bold text-ink">Enquiry received</p>
          <p className="max-w-xs text-sm leading-relaxed text-ink-3">
            Thank you. Our team will respond within one business day.
          </p>
          <KineticButton
            type="button"
            variant="tonal"
            size="sm"
            onClick={() => { setForm(emptyForm); setSent(false) }}
            className="mt-2"
          >
            Send another enquiry
          </KineticButton>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onSubmit={handleSubmit}
          className={`space-y-4 ${className}`}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" name="name" placeholder="Your name" value={form.name} onChange={handleChange} required autoComplete="name" />
            <Field label="Email" name="email" type="email" placeholder="you@company.com" value={form.email} onChange={handleChange} required autoComplete="email" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Company" name="company" placeholder="Optional" value={form.company} onChange={handleChange} autoComplete="organization" />
            <Field label="Project type" name="projectType" as="select" value={form.projectType} onChange={handleChange}>
              <option value="" className="bg-surface">Select type…</option>
              {PROJECT_TYPES.map((type) => (
                <option key={type} value={type} className="bg-surface">{type}</option>
              ))}
            </Field>
          </div>

          <Field label="Message" name="message" as="textarea" placeholder="Scope, timeline and budget range, if known…" value={form.message} onChange={handleChange} required rows={5} />

          {/* Honeypot — invisible to people, tempting to bots. */}
          <div className="hidden" aria-hidden="true">
            <input name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={handleChange} />
          </div>

          <KineticButton type="submit" disabled={busy} variant="filled" size="lg" className="w-full disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {busy ? 'Sending…' : 'Send enquiry'}
          </KineticButton>
        </motion.form>
      )}
    </AnimatePresence>
  )
}

export default ContactForm
